const START_TIMEOUT_MS = 1500;
const RETRY_DELAY_MS = 250;
const COMPLETION_GRACE_MS = 5000;
const UNKNOWN_DURATION_TIMEOUT_MS = 5 * 60 * 1000;
const POLL_MS = 200;

// Rewind during the tick interval, so Safari can finish its asynchronous seek
// before the next A is due. Do not restart a seek that is already in flight.
export function prepareSentenceAudio(audio) {
  if (!audio) return;
  audio.pause();
  if (audio.seeking !== true && Number(audio.currentTime) > 0) {
    try { audio.currentTime = 0; } catch (_) {
      // A not-yet-loaded source starts at zero once its metadata is available.
    }
  }
}

// Uses the already authorized, shared listening element. Reading owns its own
// sequencing; this controller is only for the Sentence/Short Q and A steps.
export function playSentenceAudio(audio, { reload, isCurrent = () => true }) {
  const source = audio.src;
  let resolveResult;
  const finished = new Promise(resolve => { resolveResult = resolve; });
  let settled = false;
  let active = false;
  let attempts = 0;
  let generation = 0;
  let sawStartPosition = false;
  let progressed = false;
  let lastTime = 0;
  let lastProgressAt = 0;
  let startupTimer;
  let completionTimer;
  let retryTimer;
  let poll;
  const ownsAudio = () => isCurrent() && audio.src === source;
  const clearAttemptTimers = () => {
    clearTimeout(startupTimer);
    clearTimeout(completionTimer);
  };
  const finish = (success) => {
    if (settled) return;
    const owned = ownsAudio();
    settled = true;
    active = false;
    generation += 1;
    clearAttemptTimers();
    clearTimeout(retryTimer);
    clearInterval(poll);
    audio.removeEventListener('ended', handleEnded);
    audio.removeEventListener('error', handleFailure);
    audio.removeEventListener('playing', sample);
    audio.removeEventListener('timeupdate', sample);
    audio.removeEventListener('seeked', sample);
    audio.removeEventListener('loadedmetadata', handleMetadata);
    audio.removeEventListener('durationchange', handleMetadata);
    // A failed/pending play must not start making sound after the UI pauses.
    // Never pause an element that a newer card/Reading player has taken over.
    if (!success && owned) audio.pause();
    resolveResult(success && owned);
  };
  const armCompletionTimer = () => {
    clearTimeout(completionTimer);
    const duration = Number(audio.duration);
    const rate = Math.max(0.25, Number(audio.playbackRate) || 1);
    const timeout = Number.isFinite(duration) && duration > 0
      ? Math.max(0, duration - (Number(audio.currentTime) || 0)) / rate * 1000 + COMPLETION_GRACE_MS
      : UNKNOWN_DURATION_TIMEOUT_MS;
    completionTimer = setTimeout(() => {
      sample();
      if (!settled) handleFailure();
    }, timeout);
  };
  function sample() {
    if (settled || !active) return;
    if (!ownsAudio()) { finish(false); return; }
    const time = Number(audio.currentTime);
    if (!Number.isFinite(time)) return;
    if (time <= 0.05) sawStartPosition = true;
    // Safari can still expose the previous repetition's end while rewinding.
    if (audio.seeking === true || !sawStartPosition) return;
    if (time > lastTime + 0.001) {
      lastTime = time;
      lastProgressAt = Date.now();
      if (!progressed) {
        progressed = true;
        clearTimeout(startupTimer);
        armCompletionTimer();
      }
    }
    if (!progressed) return;
    const duration = Number(audio.duration);
    const nearEnd = Number.isFinite(duration) && duration > 0 && time >= duration - 0.05;
    // Keep a missing-ended fallback, but don't cut off the tail when the next
    // clip is prepared. Near-end position alone is not completion.
    if (audio.ended === true || (nearEnd && (time >= duration || Date.now() - lastProgressAt >= 250))) {
      finish(true);
    }
  }
  function handleEnded() {
    if (!settled && active && sawStartPosition && audio.seeking !== true) finish(true);
  }
  function handleMetadata() {
    if (settled || !active) return;
    sample();
    if (!settled && progressed) armCompletionTimer();
  }
  function handleFailure() {
    if (settled || !active) return;
    active = false;
    generation += 1;
    clearAttemptTimers();
    if (!ownsAudio() || attempts >= 2) { finish(false); return; }
    audio.pause();
    retryTimer = setTimeout(() => {
      if (settled) return;
      if (!ownsAudio()) { finish(false); return; }
      try {
        reload();
        start();
      } catch (_) { finish(false); }
    }, RETRY_DELAY_MS);
  }
  function start() {
    if (!ownsAudio()) { finish(false); return; }
    attempts += 1;
    const attempt = ++generation;
    prepareSentenceAudio(audio);
    active = true;
    progressed = false;
    lastTime = 0;
    sawStartPosition = Number(audio.currentTime) <= 0.05;
    startupTimer = setTimeout(() => {
      sample();
      if (!settled && !progressed) handleFailure();
    }, START_TIMEOUT_MS);
    try {
      // A resolved play() / 'playing' event is not proof of advancing media.
      // Do not let a late promise reset a timer or retry a newer attempt.
      Promise.resolve(audio.play()).then(() => {
        if (!settled && active && attempt === generation) sample();
      }).catch(() => {
        if (!settled && active && attempt === generation) handleFailure();
      });
    } catch (_) { handleFailure(); }
  }
  audio.addEventListener('ended', handleEnded);
  audio.addEventListener('error', handleFailure);
  audio.addEventListener('playing', sample);
  audio.addEventListener('timeupdate', sample);
  audio.addEventListener('seeked', sample);
  audio.addEventListener('loadedmetadata', handleMetadata);
  audio.addEventListener('durationchange', handleMetadata);
  poll = setInterval(sample, POLL_MS);
  start();
  return { finished, cancel: () => finish(false) };
}
